import {
    PrimaryInteractiveElements,
    SecondaryInteractiveElements,
} from './constants';
import { dispatchCustomEvent } from './events';
import { insertElements, revertClassList, updateClassList } from './render-api';
import { tagRegistry } from './tag-registry';
import { getDebugMode } from './config';
import { SwipeObserver } from './swipe';
import type {
    HTMLElementOrParent,
    MaybeHTMLElement,
    MaybeHTMLElementOrParent,
    NullOr,
    PredicateFn,
    Timeout,
    UndefinedOr,
    AppendNodeConfig,
    CustomElementOptions,
    ElementControlsData,
    ThemeConfig,
    ThemeValue,
    BaseGridPoint,
    UpdateFormEventPayload,
    EventHandlerCallback,
    HTMLInputSelectOrTextElement,
} from './utils.type';

import type { UIElement } from '../components/index';
import type {
    AllowedTagName,
    AttributesConfig,
    AttributeValidationHandler,
    ElementConfig,
} from '../components/ui-element.type';
import { typedKeys } from './helpers';

export * from './config';
export * from './mutations/index';
export * from './events';
export * from './render-api';
export * from './template-literals';
export * from './loading-indicator';
export * from './constants';
export * from './tag-registry';

/**
 * Creates style element from string and adds to head.
 * @memberof UI
 * @alias addStyle
 * @deprecated Used as fallback for adoptStyle(), use adoptStyle() method.
 * @param {string} cssText
 * @param {InsertPosition} [position] Position for style tag.
 * @param {string} [id] id attribute for style.
 * @param {Element} [placement] Default is HTMLHeadElement
 * @returns {HTMLStyleElement}
 */
const addStyle = (
    cssText: string,
    position: InsertPosition = 'beforeend',
    id?: string,
    placement: Element = document.head
): HTMLStyleElement => {
    const style = document.createElement('style');
    style.setAttribute('type', 'text/css');
    if (id) {
        const styleWithId = document.querySelector<HTMLStyleElement>(
            `style#${id}`
        );
        if (styleWithId) {
            return styleWithId;
        }
        style.setAttribute('id', id);
    }

    if (placement instanceof HTMLHeadElement) {
        switch (position) {
            case 'beforebegin':
                position = 'afterbegin';
                break;
            case 'afterend':
                position = 'beforeend';
                break;
        }
    }

    placement.insertAdjacentElement(position, style);

    style.appendChild(document.createTextNode(cssText));
    return style;
};

/**
 * Adopts styles to the document.
 * @memberof UI
 * @alias adoptStyle
 * @param {string} cssText
 * @param {string} [id] needed for fallback if document.adoptedStyleSheets is not
 * supported by browser.
 * @returns {Promise<void | CSSStyleSheet[]>}
 */
const adoptStyle = async (
    cssText: string,
    id: string = ''
): Promise<void | Array<CSSStyleSheet>> => {
    if (document.adoptedStyleSheets) {
        const stylesheet = new CSSStyleSheet({ media: 'all' });
        return stylesheet
            .replace(cssText)
            .then(
                () =>
                    (document.adoptedStyleSheets = [
                        ...document.adoptedStyleSheets,
                        stylesheet,
                    ])
            )
            .catch((err) => err);
    } else {
        // #fallback, check time-to-time when it can be removed.
        addStyle(cssText, 'afterend', id);
        return Promise.resolve(void 0);
    }
};

/**
 * Search the closest parent node matched by given predicate.
 * @memberof UI
 * @alias closestByCond
 * @example
 * // Search the closest parent node with "id" equals "foo"
 * closestByCond(element, function (node) {
 *     return node.id === "id"
 * });
 * @param {Element | ParentNode} element
 * @param {Function} searchPredicate - the callback should return true value if node is matched
 * @param {Function} [stopPredicate] - if the callback returns true then search will be stopped
 * @returns {Element | ParentNode | null}
 */
const closestByCond = (
    element: HTMLElementOrParent,
    searchPredicate: PredicateFn<HTMLElementOrParent>,
    stopPredicate?: PredicateFn<HTMLElementOrParent>
): MaybeHTMLElementOrParent => {
    const searchCallback = searchPredicate || (() => true);
    const stopCallback = stopPredicate || (() => false);

    let currentElement: NullOr<typeof element> = element;
    while (currentElement) {
        if (searchCallback(currentElement)) {
            return currentElement;
        }
        if (stopCallback(currentElement)) {
            return null;
        }
        currentElement = currentElement.parentNode;
    }
    return null;
};

/**
 * Gets next siblings by conditions.
 * @memberof UI
 * @alias nextByCond
 * @param {Element} element
 * @param {Function} searchPredicate
 * @returns {Element | null}
 */
const nextByCond = (
    element: Element,
    searchPredicate: PredicateFn<Element>
): MaybeHTMLElement => {
    const searchCallback: PredicateFn<Element> | (() => true) =
        searchPredicate || (() => true);
    let nextElem: NullOr<Element> = element.nextElementSibling;

    while (nextElem) {
        if (searchCallback(nextElem)) {
            return nextElem as HTMLElement;
        }
        nextElem = nextElem.nextElementSibling;
    }
    return null;
};

/**
 * Gets previous sibling by conditions.
 * @memberof UI
 * @alias previousByCond
 * @param {Element} element
 * @param {Function} searchPredicate
 * @returns {Element | null}
 */
const previousByCond = (
    element: Element,
    searchPredicate: PredicateFn<Element>
): MaybeHTMLElement => {
    const searchCallback: PredicateFn<Element> | (() => true) =
        searchPredicate || (() => true);
    let prevElem: NullOr<Element> = element.previousElementSibling;

    while (prevElem) {
        if (searchCallback(prevElem)) {
            return prevElem as HTMLElement;
        }
        prevElem = prevElem.previousElementSibling;
    }
    return null;
};

/**
 * Gets all siblings before given element.
 * @memberof UI
 * @alias getSiblingsBefore
 * @param {Element} element
 * @returns {Element[]}
 */
const getSiblingsBefore = (element: Element): Array<Element> => {
    const previousSiblings: Array<Element> = [];
    let currentElement: NullOr<Element> = element.previousElementSibling;

    while (currentElement) {
        previousSiblings.push(currentElement);
        currentElement = currentElement.previousElementSibling;
    }

    return previousSiblings;
};

/**
 * Gets all siblings after given element.
 * @memberof UI
 * @alias getSiblingsAfter
 * @param {Element} element
 * @returns {Element[]}
 */
const getSiblingsAfter = (element: Element): Array<Element> => {
    const nextSiblings: Array<Element> = [];
    let currentElement = element.nextElementSibling;

    while (currentElement) {
        nextSiblings.push(currentElement);
        currentElement = currentElement.nextElementSibling;
    }

    return nextSiblings;
};

/**
 * Find nodes between container and desired child
 * @param {Element} rootNode root to start searching from.
 * @param {string} targetSelector selector to match elements
 * @param {string} stopSelector selector for elements to stop traversal
 * @returns {Element[]}
 */
const findNodesBetween = (
    rootNode: Element,
    targetSelector: string,
    stopSelector: string = rootNode.localName
): Array<Element> => {
    const nodes: Array<Node> = [];
    const clonedRoot = rootNode.cloneNode(true) as Element;

    /**
     * Check if a given node is within the intended scope of the rootNode.
     * @param {Element} node node to validate
     * @param {Element} clonedRoot cloned version of root
     * @param {string} targetSelector original selector, used for matching
     * @returns {boolean} true - if exists within the scoped query
     */
    const isWithinRootScope = (
        node: Element,
        clonedRoot: Element,
        targetSelector: string
    ): boolean => {
        const scopedMatches = clonedRoot.querySelectorAll(targetSelector);
        return [...scopedMatches].some((clone) => clone.isEqualNode(node));
    };

    const treeWalker = document.createTreeWalker(
        rootNode,
        NodeFilter.SHOW_ELEMENT,
        {
            acceptNode: (node: Element) => {
                if (node.matches(stopSelector) && node !== rootNode) {
                    return NodeFilter.FILTER_REJECT;
                }

                return isWithinRootScope(node, clonedRoot, targetSelector)
                    ? NodeFilter.FILTER_ACCEPT
                    : NodeFilter.FILTER_SKIP;
            },
        }
    );

    let acceptNode = treeWalker.nextNode();
    while (acceptNode) {
        nodes.push(acceptNode);
        acceptNode = treeWalker.nextNode();
    }

    return nodes as Array<Element>;
};

/**
 * Extract first found element in Array, which has needed tag name
 * @memberof UI
 * @alias extractSpecificElement
 * @param {Array} array elements, where to find
 * @param {string} tagName needed element tag name
 * @returns {HTMLElement | null}
 */
const extractSpecificElement = <T extends HTMLElement = HTMLElement>(
    array: Array<HTMLElement>,
    tagName: AllowedTagName
): NullOr<T> => {
    if (!array.length) {
        return null;
    }
    let found = null;
    const index = array.findIndex(
        (el) => el.tagName && el.localName === tagName
    );

    if (index !== -1) {
        found = array[index];
        array.splice(index, 1);
    }
    return found as T;
};

/**
 * Returns closest node with attribute 'data-event'
 * @memberof UI
 * @alias closestEventHolder
 * @param {Element} element
 * @param {Node} [stopNode]
 * @returns {Element | ParentNode | null}
 */
const closestEventHolder = (
    element: Element,
    stopNode: Node
): MaybeHTMLElementOrParent => {
    return closestByCond(
        element,
        (node: Element | ParentNode) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).hasAttribute('data-event'),
        (node: HTMLElementOrParent) => stopNode && node === stopNode
    );
};

/**
 * Returns the closest node with tag name
 * @deprecated User native Element.closest() method.
 * @memberof UI
 * @alias closestTag
 * @param {Element} element
 * @param {string} tagName
 * @param {Node} [stopNode]
 * @returns {Element | ParentNode | null}
 */
const closestTag = (
    element: Element,
    tagName: AllowedTagName,
    stopNode: Node
): MaybeHTMLElementOrParent => {
    return closestByCond(
        element,
        (node) => 'localName' in node && node.localName === tagName,
        (node) => stopNode && node === stopNode
    );
};

/**
 * Detaches child nodes from element
 * @memberof UI
 * @alias detachChildNodes
 * @param {Node} element
 * @returns {Array<HTMLElement>}
 * @protected
 */
const detachChildNodes = (element: Node): Array<HTMLElement> => {
    return [].slice.call(element.childNodes).map((node) => {
        element.removeChild(node);
        return node as HTMLElement;
    });
};

/**
 * Focus first interactive element inside the node. If the focus is already on any
 * node's child, then focus will not be changed.
 * @memberof UI
 * @alias focusInteractiveElement
 * @param {HTMLElement} element
 * @param {boolean} [skipPriorities]
 * @param {boolean} [preventScroll]
 * @returns {HTMLElement}
 */
const focusInteractiveElement = (
    element: HTMLElement,
    skipPriorities: boolean = false,
    preventScroll: boolean = false
): HTMLElement => {
    const active = document.activeElement;
    if (active && (active === element || element.contains(active))) {
        // Already has focused element
        return element;
    }

    let target: NullOr<HTMLElement>;
    if (skipPriorities) {
        target = element.querySelector(
            PrimaryInteractiveElements.concat(
                SecondaryInteractiveElements
            ).join(', ')
        );
    } else {
        target = element.querySelector(PrimaryInteractiveElements.join(', '));
        if (!target) {
            target = element.querySelector(
                SecondaryInteractiveElements.join(', ')
            );
        }
    }

    if (target) {
        target.focus({ preventScroll });
    }
    return element;
};

/**
 * Returns elements for slot
 * @memberof UI
 * @alias getSlots
 * @param {Element} element
 * @param {string} key
 * @returns {Array<HTMLElement>}
 * @protected
 */
const getSlots = (element: Element, key: string): Array<HTMLElement> => {
    return Array.from(element.children).filter((node) => {
        return node.getAttribute('slot') === key;
    }) as Array<HTMLElement>;
};

/**
 * Returns elements for slot and detaches them from DOM.
 * @memberof UI
 * @alias getChildrenForSlot
 * @param {Element} element
 * @param {string} key
 * @returns {Array<HTMLElement>}
 * @protected
 */
const getChildrenForSlot = (
    element: Element,
    key: string
): Array<HTMLElement> => {
    return getSlots(element, key).map((node: HTMLElement) => {
        node.removeAttribute('slot');
        node.parentElement?.removeChild(node);
        return node;
    });
};

/**
 * Returns the list of focusable elements for the given node.
 * @memberof UI
 * @alias getFocusableElements
 * @param {HTMLElement} element
 * @returns {Array<HTMLElement> | *}
 */
const getFocusableElements = (element: HTMLElement): Array<HTMLElement> => {
    const targets = element.querySelectorAll(
        PrimaryInteractiveElements.concat(SecondaryInteractiveElements).join(
            ', '
        )
    );
    return [].slice.call(targets, 0).filter((node) => isVisible(node));
};

/**
 * Returns the first focusable element for the given node.
 * @memberof UI
 * @alias getFirstFocusableElement
 * @param {HTMLElement} element
 * @returns {HTMLElement | null}
 */
const getFirstFocusableElement = (element: HTMLElement): MaybeHTMLElement => {
    return element.querySelector(
        PrimaryInteractiveElements.concat(SecondaryInteractiveElements).join(
            ','
        )
    );
};

/**
 * Add class -hidden to hide the element.
 * @memberof UI
 * @alias hide
 * @param {HTMLElement} element
 * @returns {HTMLElement}
 */
const hide = (element: HTMLElement): HTMLElement => {
    element?.classList.add('-hidden');
    return element;
};

/**
 * Checks if document is ready for Web Components to be rendered.
 * @memberof UI
 * @alias isDOMReady
 * @returns {boolean}
 */
const isDOMReady = (): boolean =>
    /complete|interactive|loaded/.test(document.readyState);

/**
 * Checks if the element is parent of given node.
 * @memberof UI
 * @alias isParentOf
 * @param {HTMLElement} parent element
 * @param {Element} node - given node
 * @returns {boolean} - true if current node is parent to given node
 */
const isParentOf = (parent: HTMLElement, node: Element): boolean =>
    node === parent || parent.contains(node);

/**
 * @param {HTMLElement} element
 * @memberof UI
 * @alias isVisible
 * Checks if the element is visible.
 * @returns {boolean} true if visible
 * @deprecated please use HTMLElement.prototype.checkVisibility method.
 */
const isVisible = (element: HTMLElement): boolean => {
    return !!(
        element.offsetWidth ||
        element.offsetHeight ||
        element.getClientRects().length
    );
};

/**
 * Returns the node's position in the parent DOM tree.
 * @memberof UI
 * @alias position
 * @param {Element} element
 * @returns {number}
 */
const position = (element: Element): number => {
    const children: UndefinedOr<HTMLCollection> =
        element.parentElement?.children;
    if (!children) {
        return -1;
    }
    return ([].slice.call([...children]) as Array<Element>).indexOf(element);
};

/**
 * Finds direct children matched by condition.
 * @memberof UI
 * @alias queryChildren
 * @param {Element} element
 * @param {string} selector
 * @returns {Array<HTMLElement>}
 */
const queryChildren = (
    element: Element,
    selector: string
): Array<HTMLElement> => {
    return [...element.querySelectorAll<HTMLElement>(`:scope > ${selector}`)];
};

/**
 * HTML parser needs to know the children's data.
 * So we will re-detach them to make browser renders them first.
 * @memberof UI
 * @alias redetachChildNodes
 * @param {Element} element
 * @protected
 */
const redetachChildNodes = (element: Element) => {
    insertElements(element, detachChildNodes(element));
};

/**
 * @memberof UI
 * @alias runTransition
 * @param {HTMLElement} element
 * @param {Record<string, boolean>} config - hash map where key is classname value is flag
 * @param {number} timeout
 * @returns {Promise<void>}
 */
const runTransition = async (
    element: HTMLElement,
    config: Record<string, boolean>,
    timeout: number
): Promise<void> => {
    const prepareTransition = (): Promise<void> => {
        return new Promise((resolve: (value?: void) => void): void => {
            updateClassList(element, config);
            element.setAttribute('data-transition', 'true');
            requestAnimationFrame(() => {
                updateClassList(element, {
                    '-animating': true,
                });
                resolve();
            });
        });
    };

    const waitForTransitionEnd = (timeout: Timeout) => {
        return new Promise((resolve: (value?: void) => void): void => {
            let completed = false;
            let timer: NullOr<Timeout> = null;
            const complete = () => {
                if (completed) {
                    return;
                }
                completed = true;
                if (transitionHandler) {
                    element.removeEventListener(
                        'transitionend',
                        transitionHandler
                    );
                }
                if (timer) {
                    clearTimeout(timer);
                }
                resolve();
            };
            timer = setTimeout(complete, (timeout as number) || 500);
            const transitionHandler = (e: TransitionEvent) => {
                if (completed || e.target !== element) {
                    return;
                }
                complete();
            };
            element.addEventListener('transitionend', transitionHandler);
        });
    };
    await prepareTransition();
    await waitForTransitionEnd(timeout);
    updateClassList(element, {
        '-animating': false,
    });
    revertClassList(element, config);
    element.removeAttribute('data-transition');
};

/**
 * Remove class -hidden to show the element.
 * @memberof UI
 * @alias show
 * @param {HTMLElement} element
 * @returns {HTMLElement}
 */
const show = (element: HTMLElement): HTMLElement => {
    element.classList.remove('-hidden');
    return element;
};

/**
 * Adds DOMLoaded handler.
 * @memberof UI
 * @alias whenDOMReady
 * @param {Function} callback Callback function.
 * @param {boolean} [isSync] Is rendering synchronous.
 */
const whenDOMReady = (callback: () => void, isSync: boolean) => {
    if (typeof callback !== 'function') {
        return;
    }
    const done = () => {
        if (isSync) {
            callback();
        } else {
            requestAnimationFrame(callback);
        }
    };
    if (isDOMReady()) {
        done();
    } else {
        document.addEventListener('DOMContentLoaded', done, false);
    }
};

/**
 * @typedef {object} ICustomElementOptions
 * @property {Array<string> | string | undefined} [styles] styles to be added to the document
 * @property {HTMLElement | null} [stylePlacement] where to place the styles
 */

/**
 * Defines custom element
 * @memberof UI
 * @alias defineElement
 * @param {string} tag
 * @param {CustomElementConstructor} constructor
 * @param {ICustomElementOptions} [options]
 */
const defineElement = (
    tag: string,
    constructor: CustomElementConstructor,
    options?: CustomElementOptions
) => {
    if (customElements.get(tag)) {
        return;
    }
    customElements.define(tag, constructor);
    tagRegistry.set(constructor, tag);

    const styles = options?.styles;
    const styleString = Array.isArray(styles) ? styles.join('') : styles;

    if (styleString) {
        adoptStyle(styleString, `${tag}-style`).catch((err) =>
            console.error(err)
        );
    }
};

/**
 * Merge two attributes objects.
 * @memberof UI
 * @alias mergeAttributes
 * @param {AttributesConfig} origin
 * @param {AttributesConfig} update
 * @returns {AttributesConfig}
 */
const mergeAttributes = (
    origin: AttributesConfig,
    update: AttributesConfig
): AttributesConfig => {
    const source = update || {};
    return typedKeys(source).reduce<AttributesConfig>((result, key) => {
        result[key] = source[key];
        return result;
    }, origin || {});
};

/**
 * Smoothly remove node from a DOM tree.
 * @memberof UI
 * @alias runSmoothRemove
 * @param  {UIElement | HTMLElement} node
 * @param {Record<string, boolean>} config - hash map where key is classname value is flag
 * @param {number} timeout
 * @returns {Promise<void>}
 */
const runSmoothRemove = (
    node: UIElement | HTMLElement,
    config: Record<string, boolean>,
    timeout: number
): Promise<void> => {
    if (!node.parentElement) {
        return Promise.resolve();
    }
    config['-removing'] = true;
    return runTransition(node, config, timeout).then(() => {
        if (node.parentElement) {
            node.parentElement.removeChild(node);
        }
    });
};

/**
 * Smoothly append the NODE to target element.
 * @memberof UI
 * @alias runSmoothAppend
 * @param  {UIElement | HTMLElement} node
 * @param {HTMLElement} target
 * @param {Record<string, boolean>} config - hash map where key is classname value is flag
 * @param {number} timeout
 * @returns {Promise<void>}
 */
const runSmoothAppend = (
    node: UIElement | HTMLElement,
    target: HTMLElement,
    config: AppendNodeConfig,
    timeout: number
): Promise<void | HTMLElement> => {
    hide(node);
    target.appendChild(node);
    return new Promise((resolve) => {
        config['-inserting'] = true;
        requestAnimationFrame(resolve);
    })
        .then(() => runTransition(node, config, timeout))
        .then(() => show(node));
};

/**
 * Get object data from the elements nested controls, where the name attribute is a key property.
 * @memberof UI
 * @alias getData
 * @param {HTMLElement} element
 * @returns {Record<string, string>}
 */
const getData = (element: HTMLElement): ElementControlsData => {
    const selectors = [
        'select[name]',
        'textarea[name]',
        'input[name]:not([type=radio]):not([type=checkbox])',
        'input[name][type=radio]:checked',
        'input[name][type=checkbox]:checked',
    ];
    const controls = element.querySelectorAll(selectors.join(', '));

    return Array.from(controls).reduce<ElementControlsData>(
        (data, node: Element) => {
            const nameAttribute: NullOr<string> = node.getAttribute('name');
            if (nameAttribute) {
                data[nameAttribute] =
                    'value' in node ? (node.value as string) : null;
            }
            return data;
        },
        {}
    );
};

/**
 * Set object data to the elements nested controls, where the name attribute is a key property.
 * @memberof UI
 * @alias setData
 * @param {HTMLElement} element
 * @param {Record<string, string | null>} data Data to be applied to the node.
 * @returns {HTMLElement}
 */
const setData = (
    element: HTMLElement,
    data: ElementControlsData
): HTMLElement => {
    const updatedNodes: Array<HTMLElement> = [];
    if (typeof data === 'object' && !Array.isArray(data)) {
        Object.keys(data || {}).forEach((key) => {
            [].forEach.call(
                element.querySelectorAll('[name=' + key + ']'),
                (node: HTMLElement): void => {
                    const isInput = node.nodeName?.toLowerCase() === 'input';
                    const isCheckbox =
                        isInput && node.getAttribute('type') === 'checkbox';
                    const isRadioButton =
                        isInput && node.getAttribute('type') === 'radio';
                    const hasValueProp = 'value' in node;

                    if (isCheckbox) {
                        (node as HTMLInputElement).checked = Boolean(data[key]);
                    } else if (isRadioButton) {
                        (node as HTMLInputElement).checked =
                            data[key] === (node as HTMLInputElement).value;
                    } else if (hasValueProp) {
                        node.value = data[key];
                    }
                    updatedNodes.push(node);
                }
            );
        });
        const payload: UpdateFormEventPayload = { updatedNodes };
        dispatchCustomEvent(element, 'form-update', payload);
    }
    return element;
};

/**
 * Check if given element is a form element.
 * @memberof UI
 * @alias isFormElement
 * @param {HTMLElement} elem
 * @returns {boolean}
 */
const isFormElement = (elem: HTMLElement): boolean => {
    return ['input', 'select', 'textarea'].includes(elem.localName);
};

/**
 * Mapping theme name with the presentational color in dropdown for preview.
 * Put all themes here that are active.
 * @memberof UI
 * @alias Themes
 * @type {Record<string, string>}
 */
const Themes: ThemeConfig = {
    'theme-default': 'var(--ui-color-orange)',
    'theme-fff-ish': 'var(--ui-color-surface-default-light)',
};

/**
 * Sets the theme and keep in localStorage. To unset the theme pass empty
 * string or null as first argument.
 * @memberof UI
 * @alias setTheme
 * @param {string|null} theme
 * @param {Element} [root]
 */
const setTheme = (
    theme: NullOr<ThemeValue>,
    root: Element = document.documentElement
) => {
    const storageKey = 'theme';
    if (!theme || theme.includes('default')) {
        // skip default
        root.classList.forEach((t) => {
            if (t.startsWith('theme-')) {
                root.classList.remove(t);
            }
        });
        window.localStorage.removeItem(storageKey);
        return;
    }
    if (!theme.startsWith('theme-')) {
        theme = `theme-${theme}`;
    }
    root.classList.add(theme);
    window.localStorage.setItem(storageKey, theme);
};

/**
 * Gets current design theme. If no theme returns empty string.
 * @memberof UI
 * @alias getTheme
 * @returns {string}
 */
const getTheme = (): ThemeValue => {
    const theme = window.localStorage.getItem('theme');
    return theme ? theme.trim() : '';
};

/**
 * Checks that click was done outside of element's rect.
 * @memberof UI
 * @alias doesClickedInsideRect
 * @param {HTMLElement} element
 * @param {MouseEvent | PointerEvent} mouseEvent
 * @returns {boolean}
 */
const doesClickedInsideRect = (
    element: HTMLElement,
    mouseEvent: MouseEvent | PointerEvent
): boolean => {
    const rect = element.getBoundingClientRect();
    return (
        rect.y <= mouseEvent.clientY &&
        mouseEvent.clientY <= rect.y + rect.height &&
        rect.x <= mouseEvent.clientX &&
        mouseEvent.clientX <= rect.x + rect.width
    );
};

/**
 * Gets the base grid point size.
 * @memberof UI
 * @alias getBaseGridPoint
 * @param {boolean} [useString] If true then string is returned, like "8px".
 * @returns {number | string}
 */
const getBaseGridPoint = ((useString?: boolean) => {
    return getCSSPropertyValue('--ui-grid-base-point', useString);
}) as BaseGridPoint;

/**
 * Gets CSS custom property value. Returns empty string if there is syntax error in CSS.
 * @memberof UI
 * @alias getCSSPropertyValue
 * @param {string} name Name of the property.
 * @param {boolean} [useString] If true then string is returned, like "8px".
 * @param {Element} [el] The element for which to get style, default - document.documentElement.
 * @returns {number | string}
 */
const getCSSPropertyValue = (
    name: string,
    useString: boolean = false,
    el: Element = document.documentElement
): number | string => {
    let rootStyles: CSSStyleDeclaration;
    try {
        rootStyles = window.getComputedStyle(el);
        const value = rootStyles.getPropertyValue(name);
        const num = parseInt(value, 10);
        return useString || Number.isNaN(num) ? value : num;
    } catch (e) {
        return '';
    }
};

/**
 * Adds event handler that checks that a click was done outside of element/s.
 * It returns callback to remove the event handler.
 * This function returns promise to be sure that popover isn't opened and closed
 * in the same frame (tick).
 * @memberof UI
 * @alias addOutsideClickHandler
 * @param {HTMLElement | HTMLElement[]} elements
 * @param {function(event): void} callback
 * @returns {Promise<Function>}
 */
const addOutsideClickHandler = async (
    elements: HTMLElement | Array<HTMLElement>,
    callback: EventHandlerCallback
): Promise<(...args: Array<unknown>) => void> => {
    // Problem seems to be when component that uses is pre-rendered or so, then callback is undefined!
    if (!elements || !callback) {
        return () => void 0;
    }

    const targetElements = Array.isArray(elements) ? elements : [elements];

    const clickHandler = (event: MouseEvent) => {
        const isClickOutside = !targetElements.reduce<boolean>(
            (isClickInside, element: HTMLElement) => {
                return (
                    isClickInside ||
                    element.contains(event.target as HTMLElement)
                );
            },
            false
        );
        isClickOutside && callback(event);
    };

    // TODO: Consider listening to 'mousedown' instead of 'click'. When DOM mutations between
    // mousedown and mouseup cause page scroll, the browser retargets the click to <body>.
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            document.addEventListener('click', clickHandler);
            resolve(() => {
                document.removeEventListener('click', clickHandler);
            });
        });
    });
};

/**
 * Blocks the click event bubbling up from the target element.
 * It returns a callback to remove the event handler.
 * @memberof UI
 * @alias addClickPropagationStopper
 * @param {HTMLElement} element
 * @param {string | undefined} warningMessage
 * @returns {function(): void}
 */
const addClickPropagationStopper = (
    element: HTMLElement,
    warningMessage: string = 'Element click propagation stopped!'
): (() => void) => {
    const clickHandler = (event: MouseEvent) => {
        event.stopPropagation();
        getDebugMode() && console.warn(warningMessage);
    };

    element.addEventListener('click', clickHandler);
    return () => {
        element.removeEventListener('click', clickHandler);
    };
};

/**
 * Adds the same event listener to one or more events for an element and returns a callback to remove them.
 * @memberof UI
 * @alias addMultipleEventListener
 * @param {HTMLElement} element - The element to which the event(s) listener will be added.
 * @param {string|string[]} eventNames - A single event name or an array of event names.
 * @param {function(Event): void} callback - The callback function to be called when the event(s) occur.
 * @returns {function(): void} A function when called, will remove the listener for all the added events.
 */
const addMultipleEventListener = (
    element: HTMLElement,
    eventNames: string | Array<string>,
    callback: (arg0: Event) => void
): (() => void) => {
    if (!element) {
        return () => {};
    }

    const targetEvents = Array.isArray(eventNames) ? eventNames : [eventNames];

    targetEvents.forEach((eventName: string) => {
        element.addEventListener(eventName, callback);
    });

    return () => {
        targetEvents.forEach((eventName: string) => {
            element.removeEventListener(eventName, callback);
        });
    };
};

/**
 * Filters out all elements from the element list that are not in the allowed elements.
 * @memberof UI
 * @alias keepAllowedElements
 * @param {Array<HTMLElement> | NodeList<HTMLElement>} elementList
 * @param {Array<string>} allowedElementTags
 * @returns {Array<HTMLElement>}
 */
const keepAllowedElements = (
    elementList: Array<HTMLElement> | NodeList,
    allowedElementTags: Array<string>
): Array<HTMLElement> => {
    return Array.from(elementList).filter((element: Node) =>
        allowedElementTags.includes((element as HTMLElement).localName)
    ) as Array<HTMLElement>;
};

/**
 * Filters recursively out all elements from the element list that are in the forbidden elements,
 * maintaining the original hierarchy.
 * @memberof UI
 * @alias removeForbiddenElements
 * @param {Array<HTMLElement> | NodeList<HTMLElement>} elementList
 * @param {Array<string>} forbiddenElementTags
 * @returns {Array<HTMLElement>}
 */
const removeForbiddenElements = (
    elementList: Array<HTMLElement> | NodeList,
    forbiddenElementTags: Array<string>
): Array<HTMLElement> => {
    return Array.from(elementList).reduce<Array<HTMLElement>>(
        (acc, element) => {
            if (
                !forbiddenElementTags.includes(
                    (element as HTMLElement).localName
                )
            ) {
                const clonedElement: Node = element.cloneNode(false);

                if (element.childNodes.length > 0) {
                    const filteredChildren = removeForbiddenElements(
                        element.childNodes,
                        forbiddenElementTags
                    );
                    filteredChildren.forEach((child) =>
                        clonedElement.appendChild(child)
                    );
                }

                if (
                    clonedElement.childNodes.length > 0 ||
                    element.childNodes.length === 0
                ) {
                    acc.push(clonedElement as HTMLElement);
                }
            }

            return acc;
        },
        []
    );
};

/**
 * Creates a content shift handler for a specific element.
 * This function sets up a tracker that monitors the position of the given element
 * and calls the provided callback if the element's position changes relative to document.
 * @memberof UI
 * @alias addContentShiftHandler
 * @param {HTMLElement} element - The DOM element to track for content shifts.
 * @param {function()} callback - The function to call when a content shift is detected.
 * @returns {function(): void} A cleanup function that to remove the element from the tracker with its handler.
 */
const addContentShiftHandler = (() => {
    const targetElements = new Map();
    let contentShiftTrackerId: NullOr<Timeout>;

    const tracker = () => {
        targetElements.forEach(
            ({ top, left, callback }, element: HTMLElement) => {
                const rect: DOMRect = element.getBoundingClientRect();
                const newLeft = rect.left + window.scrollX;
                const newTop = rect.top + window.scrollY;

                if (top !== newTop || left !== newLeft) {
                    targetElements.set(element, {
                        top: newTop,
                        left: newLeft,
                        callback,
                    });

                    callback();
                }
            }
        );
    };

    return (element: HTMLElement, callback: () => void) => {
        if (!contentShiftTrackerId) {
            contentShiftTrackerId = setInterval(tracker, 40);
        }

        const rect = element.getBoundingClientRect();
        targetElements.set(element, {
            top: rect.top + window.scrollX,
            left: rect.left + window.scrollY,
            callback,
        });

        return () => {
            targetElements.delete(element);

            if (targetElements.size === 0) {
                clearInterval(contentShiftTrackerId || undefined);
                contentShiftTrackerId = null;
            }
        };
    };
})();

/**
 * Returns promise to be able to wait a certain amount of milliseconds.
 * @memberof UI
 * @alias wait
 * @param {number} delay
 * @returns {Promise<void>}
 */
const wait = (delay: number = 0): Promise<void> => {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    });
};

/**
 * Checks that element has at lest 1 Node element, in other words do not have
 * only:, comments, CDATA child nodes. Also, if countSpaces is true, then spaces
 * also do not count. Hopefully someday CSS `:blank` selector will be supported
 * and after this function is pointless.
 * @memberof UI
 * @alias hasNodeOrTextChildren
 * @param {HTMLElement} element
 * @param {boolean} [countSpaces]
 * @returns {boolean}
 */
const hasNodeOrTextChildren = (
    element: HTMLElement,
    countSpaces: boolean = true
): boolean => {
    return Array.from(element.childNodes).some(
        (node) =>
            node.nodeType === Node.ELEMENT_NODE ||
            (node.nodeType === Node.TEXT_NODE &&
                countSpaces &&
                element.innerText.trim().length > 0)
    );
};

/**
 * Returns promise to be able to wait a certain amount of browser paint frames.
 * @param {number} frameCount
 * @returns {Promise<void>}
 */
const skipFrames = (frameCount: number = 1): Promise<void> => {
    return new Promise((resolve) => {
        let counter = 0;
        const frameSkipper = () => {
            requestAnimationFrame(() => {
                counter++;
                if (counter < frameCount) {
                    frameSkipper();
                } else {
                    resolve();
                }
            });
        };

        frameSkipper();
    });
};

/**
 * @typedef {object} PictureSource
 * @property {string} media - Media query.
 * @property {string} srcset - Source for an image.
 */

/**
 * Creates &lt;picture> tag with &lt;source> children and given media queries.
 * Also, can return &lt;tag> if shouldHavePicture is false, this allows to
 * reduce code duplication in various components and provide backward
 * compatibility with legacy projects.
 * @memberof UI
 * @alias createPictureWithSources
 * @deprecated Use living oak if available.
 * @param {PictureSource[]} sources Sources of the image, object where:
 * 'media' key is for media queries;
 * 'srcset' source sets corresponding to media given media queries;
 * @param {string} src - Default image source.
 * @param {string} [alt] - Alternate text for image accessibility.
 * @param {boolean} [createPictureTag] - For legacy compatibility; sometimes
 * @param {Record<string, boolean>} classList - Classes to apply to image
 * there is no need to create <picture> at all, after migration to re-designed
 *  marketing this parameter can be removed, see TODO GUI-3082
 * @returns {ElementConfig}
 */
const createPictureWithSources = (
    sources: Array<Partial<HTMLElementTagNameMap['source']>>,
    src: string,
    alt: string = '',
    createPictureTag: boolean = true,
    classList: Record<string, boolean> = {}
): ElementConfig => {
    const img: ElementConfig = {
        tagName: 'img',
        attributes: { src, alt },
        classList: classList,
    };
    if (!createPictureTag) {
        return img;
    }
    const hasMediaSources = sources.length === 0;
    return {
        tagName: 'picture',
        children: hasMediaSources
            ? [img]
            : sources
                  .map(
                      (source): ElementConfig => ({
                          tagName: 'source',
                          attributes: { ...source },
                      })
                  )
                  .concat([img]),
    };
};

/**
 * Returns the native property descriptor for `value` on the given input, select or textarea element,
 * before any instance-level patching applied by patchExternallyControlledFormProperties.
 * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} [input] Defaults to HTMLInputElement.prototype.
 * @returns {PropertyDescriptor}
 * @protected
 */
const getNativeInputDescriptor = (
    input?: HTMLInputSelectOrTextElement
): PropertyDescriptor => {
    const proto = input
        ? Object.getPrototypeOf(input)
        : HTMLInputElement.prototype;
    return Object.getOwnPropertyDescriptor(proto, 'value')!;
};

/**
 * Writes a value to an input or textarea element bypassing any patched setter,
 * to avoid triggering onValuePatched recursively during internal updates.
 * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} input
 * @param {string} value
 */
const setNativeInputValue = (
    input: HTMLInputSelectOrTextElement,
    value: string
): void => {
    getNativeInputDescriptor(input).set!.call(input, value);
};

/**
 * @param {HTMLElement} component
 * @param {string} attribute
 * @type {void}
 * @private
 */
const warnForAttributeDeprecation: AttributeValidationHandler = (
    component: HTMLElement,
    attribute: string
): void => {
    console.warn(`${attribute} - attribute is deprecated.`, component);
};

export {
    addStyle,
    adoptStyle,
    nextByCond,
    previousByCond,
    closestByCond,
    getSiblingsBefore,
    getSiblingsAfter,
    findNodesBetween,
    extractSpecificElement,
    closestEventHolder,
    closestTag,
    defineElement,
    detachChildNodes,
    focusInteractiveElement,
    getSlots,
    getChildrenForSlot,
    getFirstFocusableElement,
    getFocusableElements,
    hide,
    isDOMReady,
    isParentOf,
    isVisible,
    position,
    queryChildren,
    redetachChildNodes,
    runTransition,
    show,
    whenDOMReady,
    mergeAttributes,
    runSmoothAppend,
    runSmoothRemove,
    getData,
    setData,
    isFormElement,
    Themes,
    getTheme,
    setTheme,
    doesClickedInsideRect,
    getBaseGridPoint,
    getCSSPropertyValue,
    addOutsideClickHandler,
    addClickPropagationStopper,
    addMultipleEventListener,
    keepAllowedElements,
    removeForbiddenElements,
    addContentShiftHandler,
    wait,
    hasNodeOrTextChildren,
    skipFrames,
    warnForAttributeDeprecation,
    createPictureWithSources,
    SwipeObserver,
    getNativeInputDescriptor,
    setNativeInputValue,
};
